import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { CurrencyAutocomplete, AddFilterAction } from '~/components';
import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { Moment } from '~/types';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import cx from 'classnames';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import { resource } from '../../TransfersList';
import { TransfersFilters, TransfersSearchFilterDto } from '~/types/Transfer';
import { cleanupFormValues } from '~/utils';
import { SelectTransferCategory } from './SelectTransferCategory';
import { time } from '~/utils';
import { SelectTransactionType } from '~/components/TransactionsHistory/components/SelectTransactionType';
import MappingSystemFilter from './MappingSystemFilter';
import MappingSystemStatusFilter from './MappingSystemStatusFilter';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

//--------------------------------------------

interface Props {
  cleanFilters?: boolean;
}

const Filters = (props: Props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.transfers.list.filters.${key}`);

  const filters = useSelector(selectors.getFilters<TransfersFilters>(resource));
  const [localValues, setLocalValues] = useState<TransfersFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>(
    Object.keys(filters).filter((k) => {
      return !!(filters as any)[k];
    })
  );

  const [isInitialized, setInitialized] = useState<boolean>(false);

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: { postedAtFrom: time().subtract(1, 'day'), postedAtTo: time() },
        })
      );
      setAddedFilters([]);
    }
    setInitialized(true);
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);
  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;

    if (isInitialized) {
      setLocalValues({
        ...localValues,
        [name]: value,
      });
      debouncedDispatchFilterChange(name, value);
    }
  };

  const hideFilter = (filter: string) => {
    let inputName = filter;
    if (filter === 'transactionStatus') {
      inputName = 'status';
    }
    if (filter === 'ledgerMappingStatusBySystem') {
      dispatchFilterChange('ledgerSystem', '');
      dispatchFilterChange('ledgerSystemStatus', '');
      dispatchFilterChange('ledgerMappingStatusBySystem', '');
    }
    dispatchFilterChange(inputName, '');
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  const allFilters = [
    'currency',
    'transferOperationType',
    'transferCategory',
    'fromProfileId',
    'toProfileId',
    'transactionId',
    'transferId',
    'referenceId',
    'ledgerMappingStatusBySystem',
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'currency':
        return (
          <CurrencyAutocomplete
            label={t('currency')}
            onChange={debouncedHandleInput}
            value={localValues.currency}
            name='currency'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'transferOperationType':
        return (
          <SelectTransactionType
            label={t('transferOperationType')}
            name='transferOperationType'
            value={localValues.transferOperationType ?? ''}
            onChange={handleSelectInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'transferCategory':
        return (
          <SelectTransferCategory
            label={t('transferCategory')}
            name='transferCategory'
            value={localValues.transferCategory ?? ''}
            onChange={handleSelectInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'fromProfileId':
        return (
          <TextField
            label={t('fromProfileId')}
            name='fromProfileId'
            value={localValues.fromProfileId ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'toProfileId':
        return (
          <TextField
            label={t('toProfileId')}
            name='toProfileId'
            value={localValues.toProfileId ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'transactionId':
        return (
          <TextField
            label={t('transactionId')}
            name='transactionId'
            value={localValues.transactionId ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'transferId':
        return (
          <TextField
            label={t('transferId')}
            name='transferId'
            value={localValues.transferId ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'referenceId':
        return (
          <TextField
            label={t('referenceId')}
            name='referenceId'
            value={localValues.referenceId ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'ledgerMappingStatusBySystem':
        return (
          <>
            <MappingSystemFilter
              label={t('ledgerSystem')}
              name='ledgerSystem'
              value={localValues.ledgerSystem ?? ''}
              onChange={handleSystemChange}
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
              helperText={
                localValues.ledgerSystem && !localValues.ledgerSystemStatus
                  ? t('selectLedgerSystemHelper')
                  : ''
              }
            />
            <MappingSystemStatusFilter
              label={t('ledgerSystemStatus')}
              name='ledgerSystemStatus'
              value={localValues.ledgerSystemStatus ?? ''}
              onChange={handleSystemStatusChange}
              disabled={!localValues.ledgerSystem}
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              helperText={!localValues.ledgerSystem ? t('selectLedgerSystemStatusHelper') : ''}
            />
          </>
        );
      default:
        return null;
    }
  };

  // Handle specific filters
  const handleDateTimeChange = (data: Moment, inputType: string) => {
    setLocalValues({
      ...localValues,
      [inputType]: data,
    });
    debouncedDispatchFilterChange(inputType, data);
  };

  const handleSystemChange = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
      ledgerSystemStatus: undefined,
    });
    dispatchFilterChange(name, value);
    dispatchFilterChange('ledgerSystemStatus', '');
    if (!value) {
      dispatchFilterChange('ledgerMappingStatusBySystem', '');
    }
  };

  const handleSystemStatusChange = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });
    dispatchFilterChange(name, value);
    if (value && localValues.ledgerSystem) {
      dispatchFilterChange('ledgerMappingStatusBySystem', `${localValues.ledgerSystem}=${value}`);
    }
  };

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });
    dispatchFilterChange(name, value);
  };

  const translateFilter = (key: string) => {
    return t(key);
  };

  // Render added filters before the boolean switch
  // to ensure the switch is always at the end of the filter list
  // and to avoid missmathced filter widths
  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                clearable
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('postedAtFrom')}
                value={localValues.postedAtFrom}
                onChange={(date) => handleDateTimeChange(date as Moment, 'postedAtFrom')}
              />

              <DateTimePicker
                clearable
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('postedAtTo')}
                value={localValues.postedAtTo}
                onChange={(date) => handleDateTimeChange(date as Moment, 'postedAtTo')}
              />
              <TextField
                label={t('fromAccountNumber')}
                name='fromAccountNumber'
                value={localValues.fromAccountNumber ?? ''}
                onChange={debouncedHandleInput}
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
              />
              <TextField
                label={t('toAccountNumber')}
                name='toAccountNumber'
                value={localValues.toAccountNumber ?? ''}
                onChange={debouncedHandleInput}
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
              />
              {renderAddedFilters()}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: translateFilter(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
            <BackgroundTaskExport
              type='TRANSACTION_TRANSFER_EXPORT'
              queryArguments={handleFilters(localValues)}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const handleFilters = (filters: TransfersFilters): TransfersSearchFilterDto => {
  if (!filters) return {};
  const { postedAtFrom, postedAtTo, ledgerSystem, ledgerSystemStatus, ...rest } = filters;
  return cleanupFormValues({
    postedAtFrom: handleDate(postedAtFrom),
    postedAtTo: handleDate(postedAtTo),
    ledgerMappingStatusBySystem:
      ledgerSystem && ledgerSystemStatus ? `${ledgerSystem}=${ledgerSystemStatus}` : undefined,
    ...rest,
  });
};

const handleDate = (date?: Moment | null): string | undefined => {
  if (!date) return undefined;
  return date.toISOString();
};

const useStyles = makeStyles(() => {
  return {
    leftSide: {
      maxWidth: 'calc(100% - 250px)',
    },
    // Can change width to fit export or other buttons on the right side
    rightSide: { minWidth: 250, textAlign: 'right' },
  };
});

export default Filters;
